/** Typed calls for KPIs, the inbox and notification settings. Shapes mirror apps/api/src/kpi-routes.ts and inbox-routes.ts. */

import { apiCall, orgPath } from '../api.ts';
import type { Filter } from '../reports/reports-api.ts';

export type TargetCondition = 'GTE' | 'LTE' | 'EQ' | 'INCREASE_BY' | 'DECREASE_BY';
export type AlertCondition = 'GT' | 'GTE' | 'LT' | 'LTE' | 'EQ' | 'INCREASE_BY' | 'DECREASE_BY';
export type TargetPeriod = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
export type AlertInterval = 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'ANNUAL';
export type KpiVisual =
  'METRIC' | 'LINE' | 'SPARKLINE' | 'BAR' | 'GAUGE' | 'PIE' | 'AREA' | 'HORIZONTAL_BAR';
export type KpiStatus = 'ON_TRACK' | 'OFF_TRACK' | 'MET' | 'MISSED' | 'NO_DATA' | 'ERROR';

export interface KpiTarget {
  type: 'ONE_TIME' | 'RECURRING';
  period: TargetPeriod | null;
  condition: TargetCondition;
  value: number;
  start: string | null;
  due: string | null;
  description: string | null;
}

export interface KpiAlert {
  condition: AlertCondition;
  value: number;
  interval: AlertInterval;
  description: string | null;
}

/** What the create / edit modal sends. Values are in the metric's own unit (cents, fractions). */
export interface KpiDraft {
  business_unit_id: string;
  title: string;
  source: string;
  metric: string;
  filters: Filter[];
  secondary_dimension: string | null;
  visual: KpiVisual;
  target: KpiTarget | null;
  alert: KpiAlert | null;
}

export interface Kpi {
  id: string;
  business_unit_id: string;
  client_name: string;
  title: string;
  source: string;
  metric: string;
  filters: Filter[];
  secondary_dimension: string | null;
  visual: KpiVisual;
  target_type: 'ONE_TIME' | 'RECURRING' | null;
  target_period: TargetPeriod | null;
  target_condition: TargetCondition | null;
  target_value: number | null;
  target_start: string | null;
  target_due: string | null;
  target_description: string | null;
  alert_condition: AlertCondition | null;
  alert_value: number | null;
  alert_interval: AlertInterval | null;
  alert_description: string | null;
  last_value: number | null;
  last_alert_value: number | null;
  last_expected: number | null;
  last_target: number | null;
  last_period_from: string | null;
  last_period_to: string | null;
  last_status: KpiStatus | null;
  last_error: string | null;
  last_refreshed_at: string | null;
  created_at: string;
}

export interface InboxItem {
  id: string;
  event: string;
  title: string;
  body: string;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

export interface Preference {
  event: string;
  label: string;
  category: string;
  is_available: boolean;
  in_app: boolean;
  email: boolean;
  in_app_offered: boolean;
  email_offered: boolean;
}

/** Fired after a KPI is evaluated: it may have raised an alert, so the bell re-counts. */
export const INBOX_CHANGED = 'synanalytics:inbox-changed';
function evaluated(kpi: Kpi): Kpi {
  window.dispatchEvent(new Event(INBOX_CHANGED));
  return kpi;
}

const kpiPath = (org: string, path = '') => orgPath(org, `/kpis${path}`);
const json = (method: string, body: unknown) => ({ method, body: JSON.stringify(body) });

export const kpisApi = {
  list: (org: string, clientId: string | null) =>
    apiCall<{ kpis: Kpi[] }>(
      kpiPath(org, clientId ? `?business_unit_id=${encodeURIComponent(clientId)}` : ''),
    ).then((data) => data.kpis),
  create: (org: string, draft: KpiDraft) =>
    apiCall<{ kpi: Kpi }>(kpiPath(org), json('POST', draft)).then((data) => evaluated(data.kpi)),
  update: (org: string, id: string, draft: KpiDraft) =>
    apiCall<{ kpi: Kpi }>(kpiPath(org, `/${id}`), json('PUT', draft)).then((data) =>
      evaluated(data.kpi),
    ),
  remove: (org: string, id: string) =>
    apiCall<{ deleted: boolean }>(kpiPath(org, `/${id}`), { method: 'DELETE' }),
  duplicate: (org: string, id: string) =>
    apiCall<{ kpi: Kpi }>(kpiPath(org, `/${id}/duplicate`), { method: 'POST' }).then((data) =>
      evaluated(data.kpi),
    ),
  refresh: (org: string, id: string) =>
    apiCall<{ kpi: Kpi }>(kpiPath(org, `/${id}/refresh`), { method: 'POST' }).then((data) =>
      evaluated(data.kpi),
    ),
  suggest: (org: string, draft: KpiDraft) =>
    apiCall<{ value: number | null; range: { from: string; to: string } }>(
      kpiPath(org, '/suggest-target'),
      json('POST', { ...draft, target: null, alert: null }),
    ),
};

export const inboxApi = {
  list: (org: string) => apiCall<{ items: InboxItem[]; unread: number }>(orgPath(org, '/inbox')),
  unread: (org: string) =>
    apiCall<{ unread: number }>(orgPath(org, '/inbox/unread-count')).then((data) => data.unread),
  read: (org: string, id: string) =>
    apiCall<{ unread: number }>(orgPath(org, `/inbox/${id}/read`), { method: 'POST' }),
  readAll: (org: string) =>
    apiCall<{ unread: number }>(orgPath(org, '/inbox/read-all'), { method: 'POST' }),
  preferences: (org: string) =>
    apiCall<{ preferences: Preference[] }>(orgPath(org, '/notification-preferences')).then(
      (data) => data.preferences,
    ),
  savePreferences: (org: string, preferences: Preference[]) =>
    apiCall<{ preferences: Preference[] }>(
      orgPath(org, '/notification-preferences'),
      json('PUT', { preferences }),
    ).then((data) => data.preferences),
};
